import React, { useState, useEffect } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AppButton from "../common/buttons/AppButton";
import FormTextField from "../common/form/FormTextField";
import CustomTypography from "../common/form/CustomTypography";
import {
  Product,
  StockCheckSuggestedProducts,
} from "../../../models/Stock/Stock";
import AppDropDown from "../common/filter/AppDropDown";
import { FilterValue } from "../../../models/Filters/FilterValue";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useStores } from "../../../use-stores";
import AppSearchDropDown from "../common/filter/AppSearchDropDown";
import { SortType } from "../../../models/SortType";
import { observer } from "mobx-react";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";


interface SearchTextState {
  productSearch: string;
  vendorSearch: string;
}

interface StockCheckDetailsPopupProps {
  open: boolean;
  onClose: () => void;
  requestId: number;
  product: Product;
  onSubmit: (
    requestId: number,
    updatedProduct: Product[],
    suggestedProduct: StockCheckSuggestedProducts[]
  ) => void;
  isViewOnly?: boolean;
}

const InStock: FilterValue[] = [
  { id: "-1", value: "Select" },
  { id: "0", value: "No" },
  { id: "1", value: "Yes" },
];

const StockCheckDetailsPopup: React.FC<StockCheckDetailsPopupProps> = ({
  open,
  onClose,
  requestId,
  product,
  onSubmit,
  isViewOnly = false,
}) => {
  const theme = useTheme();

  const { productStore, vendorStore, stockStore, userStore } = useStores();
  const [productData, setProductData] = useState<Product>(() => ({
    ...product,
  }));

  const [updatedSuggestedProducts, setUpdatedSuggestedProducts] = useState<
    StockCheckSuggestedProducts[] | []
  >([]);

  const [searchTextStates, setSearchTextStates] = useState<SearchTextState[]>(
    []
  );

  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    setProductData({ ...product });
  }, [product]);

  useEffect(() => {
    if (open) {
      stockStore.setVendorSearchInput("");
      stockStore.setUseManualVendorEntry(false);
    }
  }, [open, product, stockStore]);

  const handleInputChange = (field: keyof Product, value: string | number) => {
    const shouldClearSuggestedProducts =
      field === "in_stock" && Number(value) !== 0;

    setProductData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (shouldClearSuggestedProducts) {
      setUpdatedSuggestedProducts([]);
      setSearchTextStates([]);
    }
  };

  const handleSuggestedProductChange = (
    index: number,
    field: keyof StockCheckSuggestedProducts,
    value: string | number
  ): void => {
    setUpdatedSuggestedProducts((prev) =>
      (prev || []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const handleProductSelect = (index: number, val: FilterValue[]) => {
    if (val.length > 0) {
      const selectedProduct = val[0];
      setUpdatedSuggestedProducts((prev) =>
        (prev || []).map((item, i) =>
          i === index
            ? {
              ...item,
              id: Number(selectedProduct.id),
              product_name: selectedProduct.value,
            }
            : item
        )
      );
    } else {
      setUpdatedSuggestedProducts((prev) =>
        (prev || []).map((item, i) =>
          i === index
            ? {
              ...item,
              id: 0,
              product_name: "",
            }
            : item
        )
      );
    }
  };

  const handleVendorSelect = (index: number, val: FilterValue[]) => {
    if (val.length > 0) {
      const selectedProduct = val[0];
      setUpdatedSuggestedProducts((prev) =>
        (prev || []).map((item, i) =>
          i === index
            ? {
              ...item,
              vendor_id: Number(selectedProduct.id),
              vendor_name: selectedProduct.value,
            }
            : item
        )
      );
    } else {
      setUpdatedSuggestedProducts((prev) =>
        (prev || []).map((item, i) =>
          i === index
            ? {
              ...item,
              vendor_id: 0,
              vendor_name: "",
            }
            : item
        )
      );
    }
  };

  useEffect(() => {
    if (updatedSuggestedProducts) {
      setSearchTextStates(
        updatedSuggestedProducts.map(() => ({
          productSearch: "",
          vendorSearch: "",
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSuggestedProducts?.length]);

  const handleAddSuggestedProduct = (): void => {
    setUpdatedSuggestedProducts((prev) => [
      ...(prev || []),
      {
        id: 0,
        product_id: product.product_id,
        timeline: 0,
        vendor_id: 0,
        vendor_stock: 0,
        product_name: "",
        vendor_name: "",
        cost_price: 0,
      } as StockCheckSuggestedProducts,
    ]);

    setSearchTextStates((prev) => [
      ...prev,
      { productSearch: "", vendorSearch: "" },
    ]);
  };

  const handleDeleteSuggestedProduct = (index: number): void => {
    setUpdatedSuggestedProducts((prev) =>
      (prev || []).filter((_, i) => i !== index)
    );

    setSearchTextStates((prev) => (prev || []).filter((_, i) => i !== index));
  };

  const updateSearchText = (
    index: number,
    type: "product" | "vendor",
    value: string
  ) => {
    setSearchTextStates((prev) =>
      prev.map((state, i) =>
        i === index
          ? {
            ...state,
            [type === "product" ? "productSearch" : "vendorSearch"]: value,
          }
          : state
      )
    );
  };

  let apiTimer: any = 0;
  const handleSearchProduct = (searchText: string, index: number) => {
    if (apiTimer) clearTimeout(apiTimer);

    updateSearchText(index, "product", searchText || "");

    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        const params = {
          name: searchText,
          sortBy: "created_date",
          sortType: SortType.DESC,
          page_no: 1,
        };
        productStore.getProducts(params);
      }, 1000);
  };

  const handleSearchVendor = (searchText: string, index: number) => {
    if (apiTimer) clearTimeout(apiTimer);

    updateSearchText(index, "vendor", searchText || "");

    if (searchText && searchText.length > 2) {
      apiTimer = setTimeout(() => {
        const params = {
          search_text: searchText,
          page_no: 1,
        };
        vendorStore.getVendors(params);
      }, 1000);
    }
  };

  const validateForm = (): boolean => {
    if (parseInt(productData.in_stock!?.toString()) === 1) {
      if (!productData.vendor_name?.trim() || !productData.timeline) {
        setErrorMessage("Please fill in all required fields");
        return false;
      }
    } else if (parseInt(productData.in_stock!?.toString()) === 0) {
      if (updatedSuggestedProducts.length > 0) {
        const invalidProducts = updatedSuggestedProducts
          .map((product, index) => {
            const missingFields = [];
            if (!product.product_name) missingFields.push("Product");
            if (!product.vendor_name) missingFields.push("Vendor");
            if (!product.vendor_stock) missingFields.push("Stock Quantity");
            if (!product.timeline) missingFields.push("Timeline");

            return missingFields.length
              ? `Product Option ${index + 1}: ${missingFields.join(", ")}`
              : null;
          })
          .filter(Boolean);

        if (invalidProducts.length) {
          setErrorMessage(`Please fill in all required fields`);
          return false;
        }
      }
    }

    setErrorMessage("");
    return true;
  };

  useEffect(() => {
    setErrorMessage("");
  }, [productData, updatedSuggestedProducts]);

  const handleConfirm = (): void => {
    if (validateForm()) {
      if (parseInt(productData.in_stock!?.toString()) === 0) {
        onSubmit(requestId, [productData], updatedSuggestedProducts || []);
      } else if (parseInt(productData.in_stock!?.toString()) === 1) {
        onSubmit(requestId, [productData], []);
      }
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          mt: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <b>Stock Check Request Details</b>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            width: "14px",
            height: "14px",
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon sx={{ fontSize: "20px" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 6, mt: 2 }}>
          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              Holding Period
            </CustomTypography>
            <FormTextField
              sx={{ mb: 3 }}
              label="Holding Period"
              value={productData.holding_period || ""}
              variant="outlined"
              onChange={(id, value) => {
                handleInputChange("holding_period", parseInt(value!) || 0);
              }}
              disabled={isViewOnly}
            />
          </Box>

          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              In stock
            </CustomTypography>
            <AppDropDown
              borderColor={theme.palette.customColors.blackBorder}
              value={
                parseInt(productData.in_stock!?.toString()) === 1
                  ? InStock[2]?.id
                  : parseInt(productData.in_stock!?.toString()) === 0
                    ? InStock[1]?.id
                    : InStock[0]?.id
              }
              list={InStock}
              isMultiSelect={false}
              title=""
              onSingleSelectionChange={(val) => {
                handleInputChange("in_stock", val);
              }}
              disabled={isViewOnly}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 6, mt: 1 }}>
          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              Vendor Name
            </CustomTypography>

            <AppSearchDropDown
              borderColor={theme.palette.customColors.blackBorder}
              isMultiSelect={false}
              required={true}
              title="Vendor"
              maxWidth="248px"
              fontSize="0.9rem"
              textFieldValue={stockStore.vendorSearchInput}
              list={[
                ...vendorStore.vendors.map((item) => ({
                  id: String(item.id),
                  value: item.name!,
                })),
                { id: "other", value: "Other" },
              ]}
              value={
                stockStore.useManualVendorEntry
                  ? [
                    {
                      id: "other",
                      value: "Other",
                    },
                  ]
                  : productData.vendor_name
                    ? [
                      {
                        id: String(
                          productData.vendor_id
                            ? productData.vendor_id
                            : "selected_" + productData.vendor_name
                        ),
                        value: productData.vendor_name,
                      },
                    ]
                    : []
              }
              local={false}
              handleSearch={(text) => {
                // Always update the input text regardless of length
                stockStore.setVendorSearchInput(text || "");
                if (text && text.toLowerCase() !== "other" && text.length > 2) {
                  const params = {
                    search_text: text,
                    page_no: 1,
                  };
                  vendorStore.getVendors(params);
                }
              }}
              handleSelect={(val: FilterValue[]) => {
                if (val.length > 0) {
                  const selectedVendor = val[0];
                  if (selectedVendor.value === "Other") {
                    stockStore.setUseManualVendorEntry(true);
                    handleInputChange("vendor_name", "");
                    stockStore.setVendorSearchInput("");
                  } else {
                    stockStore.setUseManualVendorEntry(false);
                    handleInputChange("vendor_name", selectedVendor.value);
                    stockStore.setVendorSearchInput("");
                  }
                } else {
                  stockStore.setUseManualVendorEntry(false);
                  handleInputChange("vendor_name", "");
                  stockStore.setVendorSearchInput("");
                }
              }}
              disabled={isViewOnly}
            />

            {stockStore.useManualVendorEntry && (
              <FormTextField
                required={true}
                label="Custom Vendor Name"
                value={productData.vendor_name || ""}
                variant="outlined"
                sx={{ mb: 3, maxWidth: "248px", mt: 2 }}
                onChange={(id, value) => {
                  handleInputChange("vendor_name", value!);
                }}
                disabled={isViewOnly}
              />
            )}
          </Box>

          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              Timeline
            </CustomTypography>
            <FormTextField
              label="Timeline"
              required={true}
              value={productData.timeline || ""}
              variant="outlined"
              type="number"
              onChange={(id, value) => {
                handleInputChange("timeline", value!);
              }}
              disabled={isViewOnly}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 6, mt: 1 }}>
          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              Stock Quantity
            </CustomTypography>

            <FormTextField
              label="Stock Quantity"
              value={productData.vendor_stock || ""}
              variant="outlined"
              type="number"
              sx={{ mb: 2 }}
              onChange={(id, value) => {
                handleInputChange("vendor_stock", value!);
              }}
              disabled={isViewOnly}
            />
          </Box>
          {((productData.vendor_id &&
            productData?.vendor_id !==
            Number(process.env.REACT_APP_VENDOR_ID)) ||
            (productData.vendor_name &&
              productData.vendor_name.length > 0 &&
              productData.vendor_name.toLowerCase() !== "swageazy")) &&
            userStore.hasPermission(EmployeeRoles.CHANGE_COST_PRICE) && (
              <Box sx={{ width: "50%" }}>
                <CustomTypography
                  variant="heading6"
                  style={{
                    marginBottom: "1rem",
                    color: theme.palette.customColors.greyTextColor,
                  }}
                >
                  Cost Price
                </CustomTypography>
                <FormTextField
                  label="Cost Price"
                  required={true}
                  value={productData.cost_price || ""}
                  variant="outlined"
                  type="number"
                  onChange={(id, value) => {
                    const numValue = parseFloat(value!) || 0;
                    if (numValue < 0) {
                      return;
                    }
                    handleInputChange("cost_price", numValue);
                  }}
                  disabled={isViewOnly}
                />
              </Box>
            )}
        </Box>
        <Box sx={{ width: "100%", mt: 2 }}>
          <CustomTypography
            variant="heading6"
            style={{
              marginBottom: "1rem",
              color: theme.palette.customColors.greyTextColor,
            }}
          >
            Notes (max 200 characters)
          </CustomTypography>
          <FormTextField
            label="Notes"
            value={productData.sourcing_notes || ""}
            variant="outlined"
            multiline
            maxLength={200}
            onChange={(id, value) => {
              handleInputChange("sourcing_notes", value!);
            }}
            sx={{ mb: 3, width: "100%" }}
            disabled={isViewOnly}
          />
        </Box>

        <hr
          style={{
            marginTop: "1rem",
            border: `0.5px solid ${theme.palette.customColors.grayBackGroundColor}`,
            borderStyle: "solid",
          }}
        />

        {parseInt(productData.in_stock!?.toString()) === 0 && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2, mt: 2 }}
            >
              <CustomTypography variant="heading4">
                Suggest Products
              </CustomTypography>

              {!isViewOnly && (
                <AppButton
                  startIcon={<AddIcon />}
                  variant="primary"
                  onClick={() => handleAddSuggestedProduct()}
                  label="Add Products"
                  customStyles={{
                    backgroundColor: "transparent",
                    padding: 1,
                    margin: 0,
                    fontSize: "0.9rem",
                    color: theme.palette.customColors.pinkDark,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: theme.palette.customColors.pinkDark,
                      boxShadow: "none",
                    },
                  }}
                />
              )}
            </Box>

            <Box>
              {(updatedSuggestedProducts || []).map(
                (suggestedProduct, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                    }}
                  >
                    <Box display="flex">
                      <Accordion
                        sx={{
                          flex: 1,
                          boxShadow: "none",
                          position: "relative",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`panel${index}-content`}
                          id={`panel${index}-header`}
                          sx={{
                            border: `1px solid ${theme.palette.customColors.grayBackGroundColor}`,
                            borderRadius: "4px",
                            backgroundColor: `${theme.palette.customColors.grayBackGroundColor}`,
                            padding: "0px 16px",
                            minHeight: "40px",
                            maxHeight: "45px",
                          }}
                          style={{
                            minHeight: "40px",
                            maxHeight: "45px",
                          }}
                        >
                          <CustomTypography
                            variant="heading6"
                            color={`${theme.palette.customColors.greyTextColor}`}
                            fontWeight={700}
                          >
                            Product Option {index + 1}
                          </CustomTypography>
                        </AccordionSummary>
                        <AccordionDetails style={{ padding: "1rem 0px" }}>
                          <Box
                            display="grid"
                            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                            gap={4}
                          >
                            <Box sx={{ mt: 1 }}>
                              <CustomTypography
                                variant="heading6"
                                style={{
                                  color:
                                    theme.palette.customColors.greyTextColor,
                                }}
                              >
                                Product
                              </CustomTypography>

                              <Box sx={{ mt: 2, maxWidth: "238px" }}>
                                <AppSearchDropDown
                                  borderColor={
                                    theme.palette.customColors.blackBorder
                                  }
                                  isMultiSelect={false}
                                  required={true}
                                  title="Product"
                                  value={
                                    suggestedProduct.product_name
                                      ? [
                                        {
                                          id: String(suggestedProduct.id),
                                          value:
                                            suggestedProduct.product_name,
                                        },
                                      ]
                                      : []
                                  }
                                  fontSize="0.9rem"
                                  list={
                                    productStore.products.map((item) => ({
                                      id: String(item.id),
                                      value: item.name!,
                                    })) || []
                                  }
                                  local={false}
                                  handleSearch={(text) =>
                                    handleSearchProduct(text!, index)
                                  }
                                  handleSelect={(val: FilterValue[]) => {
                                    handleProductSelect(index, val);
                                    updateSearchText(index, "product", "");
                                  }}
                                  key={`product-${index}`}
                                  textFieldValue={
                                    searchTextStates[index]?.productSearch || ""
                                  }
                                  disabled={isViewOnly}
                                />
                              </Box>
                            </Box>

                            <Box sx={{ mt: 1 }}>
                              <CustomTypography
                                variant="heading6"
                                style={{
                                  color:
                                    theme.palette.customColors.greyTextColor,
                                }}
                              >
                                Vendor
                              </CustomTypography>

                              <Box sx={{ mt: 2, maxWidth: "238px" }}>
                                <AppSearchDropDown
                                  borderColor={
                                    theme.palette.customColors.blackBorder
                                  }
                                  isMultiSelect={false}
                                  required={true}
                                  title="Vendor"
                                  value={
                                    suggestedProduct.vendor_name
                                      ? [
                                        {
                                          id: String(
                                            suggestedProduct.vendor_id
                                          ),
                                          value: suggestedProduct.vendor_name,
                                        },
                                      ]
                                      : []
                                  }
                                  fontSize="0.9rem"
                                  list={
                                    vendorStore.vendors.map((item) => ({
                                      id: String(item.id),
                                      value: item.name!,
                                    })) || []
                                  }
                                  local={false}
                                  handleSelect={(val: FilterValue[]) => {
                                    handleVendorSelect(index, val);
                                    updateSearchText(index, "vendor", "");
                                  }}
                                  handleSearch={(text) =>
                                    handleSearchVendor(text!, index)
                                  }
                                  key={`vendor-${index}`}
                                  textFieldValue={
                                    searchTextStates[index]?.vendorSearch || ""
                                  }
                                  disabled={isViewOnly}
                                />
                              </Box>
                            </Box>

                            <Box sx={{ marginTop: "-10px" }}>
                              <CustomTypography
                                variant="heading6"
                                style={{
                                  color:
                                    theme.palette.customColors.greyTextColor,
                                }}
                              >
                                Stock Quantity
                              </CustomTypography>
                              <FormTextField
                                label="Quantity"
                                required={true}
                                value={suggestedProduct.vendor_stock || ""}
                                onChange={(e, val) =>
                                  handleSuggestedProductChange(
                                    index,
                                    "vendor_stock",
                                    parseInt(val!)
                                  )
                                }
                                variant="outlined"
                                type="number"
                                sx={{ mb: 1, mt: 2 }}
                                disabled={isViewOnly}
                              />
                            </Box>

                            <Box sx={{ marginTop: "-10px" }}>
                              <CustomTypography
                                variant="heading6"
                                style={{
                                  color:
                                    theme.palette.customColors.greyTextColor,
                                }}
                              >
                                Timeline
                              </CustomTypography>
                              <FormTextField
                                label="Timeline"
                                required={true}
                                value={suggestedProduct.timeline || ""}
                                onChange={(e, val) =>
                                  handleSuggestedProductChange(
                                    index,
                                    "timeline",
                                    parseInt(val!)
                                  )
                                }
                                variant="outlined"
                                type="number"
                                sx={{ flex: 1, mb: 1, mt: 2 }}
                                disabled={isViewOnly}
                              />
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>

                      {!isViewOnly && (
                        <IconButton
                          color="secondary"
                          sx={{
                            marginLeft: "8px",
                            mt: 1,
                            backgroundColor:
                              theme.palette.customColors.pinkLight,
                            width: 30,
                            height: 30,
                          }}
                          onClick={() => handleDeleteSuggestedProduct(index)}
                        >
                          <DeleteIcon
                            sx={{
                              color: theme.palette.customColors.pinkDark,
                              width: 20,
                              height: 20,
                            }}
                          />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                )
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: "column",
          alignItems: "flex-end",
          p: 3,
          pt: 0,
          gap: 2,
        }}
      >
        {errorMessage && (
          <Box
            sx={{
              width: "100%",
              color: theme.palette.error.main,
              borderRadius: 1,
              mb: 0,
              textAlign: "center",
            }}
          >
            {errorMessage.split("\n").map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 2 }}>
          <AppButton variant="grayscale" onClick={onClose} label="Cancel" />
          {!isViewOnly && (
            <AppButton
              variant="primary"
              onClick={handleConfirm}
              label="Confirm"
              disabled={
                Number(productData.in_stock) < 0 ||
                productData.in_stock === null
              }
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default observer(StockCheckDetailsPopup);
